import Link from "next/link";

import {
  getTemporalLabel,
  getRecurringChannelStartDate,
} from "@qoohoo/common/helpers/channel";
import { getInitialsOfName } from "@qoohoo/common/utils/string";
import * as Track from "@qoohoo/common/utils/analytics/track";
import { link, pageview } from "@qoohoo/common/constants/analytics";

export default function ChannelCard({ post, isProfileOwner, isCreator }) {
  const channelType = post.type;

  const isCustomPlan = post.meta_info.interval !== null;
  const isLifetimePlan = post.meta_info.duration === "lifetime";
  
  let planType = post.meta_info.recurring_type;
  
  if(isCustomPlan) {
    planType = "custom";
  } else if (isLifetimePlan) {
    planType = "lifetime";
  }
  
  let channelTemporalDetails = getTemporalLabel({
    channelType,
    start_date: post.meta_info.scheduled_start_date || post.start_date,
    end_date: post.meta_info.scheduled_end_date || post.end_date,
    planDetails: {
      planType: planType,
      interval: post.meta_info.interval,
      duration: post.meta_info.duration,
    },
  });

  // TODO: refactor once recurring_type is deprecated in favour of plan_type on BE.
  if (channelType === "recurring" && !channelTemporalDetails) {
    channelTemporalDetails = "Subscription";
  }

  const isFreeSubscription = channelType === "free_subscription";
  const recurringChannelStartDateLabel =
    channelType === "recurring" &&
    getRecurringChannelStartDate(post.meta_info.scheduled_start_date);

  const channelImageUrl = post.image;

  return (
    <Link
      href={`/channels/${post.uid}`}
      passHref
      onClick={() => {
        Track.linkClick(link.channel_card_link, {
          url: `/channels/${post.uid}`,
          uid: post.uid,
          name: post.name,
          current_screen: pageview.profile,
        });
      }}
      target="_blank"
    >
      <li className="group relative flex flex-col px-3 py-4 mb-3 lg:mb-4 rounded-2xl border border-[#2B353B]">
        <div className="flex">
          <div className="relative w-20 h-20 lg:w-[136px] lg:h-[136px] max-w-[136px] max-h-[136px] object-contain rounded-xl mr-3 bg-[#2A2E32]">
            <div className="flex justify-center items-center w-full h-full rounded-xl overflow-hidden">
              {!!channelImageUrl ? (
                <img
                  src={post.image}
                  alt={post.name}
                  className="object-cover object-center w-full h-full"
                />
              ) : (
                <span className="md:text-[40px] text-[32px] font-bold uppercase">
                  {getInitialsOfName(post.name)}
                </span>
              )}
            </div>
            {post.is_telegram && (
              <img
                src="/assets/social-icons/telegram-original.svg"
                alt=""
                className="w-7 h-7 md:w-8 md:h-8 absolute -bottom-2 -right-2 border-4 border-[#16191B] rounded-full"
              />
            )}
            {post.is_whatsapp && (
              <img
                src="/assets/social-icons/whatsapp-original.svg"
                alt=""
                className="w-7 h-7 md:w-8 md:h-8 absolute -bottom-2 -right-2 border-4 border-[#16191B] rounded-full"
              />
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold lg:text-lg lg:leading-[25px]">
              {post.name}
            </h3>
            {channelTemporalDetails && (
              <ul className="mt-2 lg:mt-3">
                <li className="flex text-[#C4C4C4] text-xs">
                  <img
                    src="/assets/icons/calendar.svg"
                    alt="calendar icon"
                    className="w-4 h-4 mr-1.5"
                  />
                  <span className="text-xs sm:text-[13px] tracking-tight sm:tracking-normal truncate">
                    {channelTemporalDetails}
                    {recurringChannelStartDateLabel && (
                      <>
                        {" "}
                        <span className="text-[9px]">•</span>{" "}
                        {recurringChannelStartDateLabel}
                      </>
                    )}
                  </span>
                </li>
              </ul>
            )}
            <ul className="inline-flex flex-wrap max-w-full">
              {isFreeSubscription && (
                <li className="mt-3 mr-3 lg:mt-4 px-2 py-1.5 text-[11px] lg:text-xs lg:leading-[14px] bg-[#34414B] rounded-3xl">
                  🙌 FREE Membership
                </li>
              )}
              {post?.total_seats_left ? (
                <li className="mt-3 mr-3 lg:mt-4 px-2 py-1.5 text-[11px] lg:text-xs lg:leading-[14px] bg-[#34414B] rounded-3xl">
                  ✨ Limited Seats
                </li>
              ) : null}
              {post?.is_private && (
                <li className="mt-3 lg:mt-4 px-2 py-1.5 text-[11px] lg:text-xs lg:leading-[14px] bg-[#34414B] rounded-3xl">
                  🔒 Private
                </li>
              )}
            </ul>
          </div>
        </div>
        <div className="lg:absolute bottom-5 right-5 p-1.5 lg:px-4 lg:py-3 rounded-full bg-white/5 ml-auto mt-3 lg:mt-0 lg:flex justify-center items-center group-hover:opacity-80">
          <span className="hidden mr-2 lg:block font-semibold text-[13px] leading-4">
            {!isCreator ? "VISIT" : isProfileOwner ? "VISIT" : "JOIN NOW"}
          </span>
          <img
            src="/assets/icons/arrow-right.svg"
            alt="right arrow"
            className="w-4 h-4 group-hover:translate-x-1 transition-transform"
          />
        </div>
      </li>
    </Link>
  );
}
